import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import {
  MapPin,
  Calendar,
  Users,
  Car,
  FileText,
  Clock,
  Plus,
  Trash2,
  Download,
  Edit,
  Eye,
  Save,
  Send,
  Info,
  X,
  Loader2,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  Upload,
  Paperclip,
  Navigation,
  Sparkles,
  Search,
  RefreshCw,
  Table,
  UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import ManagerApprovalSelector from '../components/travel/ManagerApprovalSelector';
import LocalTravelApprovalInbox from './travel/LocalTravelApprovalInbox';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DUTY_TYPES = [
  'Local Travel',
  'Site Visit',
  'Client Visit',
  'Office Work',
  'Official Meeting',
  'Other Official Work',
];

const TRAVEL_MODES = [
  'Own Two-Wheeler',
  'Own Car',
  'Auto / Cab',
  'Bus',
  'Train',
  'Other',
];

const StatusBadge = ({ status }) => {
  const st = (status || 'DRAFT').toUpperCase();
  const meta = {
    DRAFT: { label: 'Draft', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
    SUBMITTED: { label: 'Submitted / Review', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    FINANCE_REVIEW: { label: 'Finance Review', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    APPROVED: { label: 'Approved', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
    PAYMENT_PENDING: { label: 'Payment Pending', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
    PAID: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    REJECTED: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
    SENT_BACK: { label: 'Sent Back / Rework', cls: 'bg-orange-100 text-orange-800 border-orange-300' },
    RETURNED: { label: 'Sent Back / Rework', cls: 'bg-orange-100 text-orange-800 border-orange-300' },
    CANCELLED: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    DELETED: { label: 'Deleted', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  }[st] || { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-200' };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border tracking-wider ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

export default function ESSLocalTravel() {
  const [mainTab, setMainTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'approval' || tabParam === 'inbox' || window.location.pathname.includes('/approval')) {
      return 'approval_inbox';
    }
    return 'my_claims';
  });

  const [profile, setProfile] = useState({
    employee_name: 'Loading...',
    employee_code: '...',
    company_name: '...',
    department_name: '...',
    designation_name: '...',
    reporting_manager_name: '...',
  });

  const [editingId, setEditingId] = useState(null);
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));

  // Form Master State
  const [travelPurpose, setTravelPurpose] = useState('Local Travel');
  const [defaultTravelMode, setDefaultTravelMode] = useState('Own Two-Wheeler');
  const [vehicleNumber, setVehicleNumber] = useState('');

  // Linked Travel Segments State (Google Maps Style)
  const [segments, setSegments] = useState([
    {
      segment_number: 1,
      from_location: 'Head Office',
      to_location: '',
      distance_km: '',
      travel_mode: 'Own Two-Wheeler',
      vehicle_number: '',
      start_odometer: '',
      end_odometer: '',
      rate_per_km: 5,
      conveyance_cost: '',
    },
  ]);

  // Food & Other Expenses State
  const [foodExpense, setFoodExpense] = useState(0);
  const [parkingExpense, setParkingExpense] = useState(0);
  const [tollExpense, setTollExpense] = useState(0);
  const [otherExpense, setOtherExpense] = useState(0);
  const [otherExpenseDesc, setOtherExpenseDesc] = useState('');

  // Attachments List
  const [attachments, setAttachments] = useState([]);

  // Reports & History State
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({
    total_on_duty_days: 0,
    total_travel_entries: 0,
    total_km: 0,
    total_conveyance: 0,
    total_food: 0,
    total_other: 0,
    total_claimed: 0,
    approved_amount: 0,
    pending_amount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Rework and Reapplication Workflow States
  const [reworkMode, setReworkMode] = useState({ isRework: false, returnRemarks: '', sentBackBy: '', originalReportNumber: '' });
  const [reapplicationMode, setReapplicationMode] = useState({ isReapplication: false, rejectedFromId: '', reportNumber: '', rejectionReason: '' });
  const [reworkNotes, setReworkNotes] = useState('');

  // History Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  // View Modal State
  const [viewingReport, setViewingReport] = useState(null);

  // Active Policy State
  const [activePolicy, setActivePolicy] = useState(null);
  const [dateValidation, setDateValidation] = useState(null);

  // Beneficiary Employee State for On-Behalf Claims (Employee ID Search Only)
  const [isSelfRequest, setIsSelfRequest] = useState(true);
  const [beneficiaryEmployeeId, setBeneficiaryEmployeeId] = useState('');
  const [beneficiarySearchCodeInput, setBeneficiarySearchCodeInput] = useState('');
  const [validatingBeneficiary, setValidatingBeneficiary] = useState(false);
  const [validatedBeneficiaryObj, setValidatedBeneficiaryObj] = useState(null);
  const [employeesDirectory, setEmployeesDirectory] = useState([]);

  // Approval Manager Selection State
  const [eligibleManagers, setEligibleManagers] = useState([]);
  const [selectedApproverId, setSelectedApproverId] = useState('');
  const [loadingManagers, setLoadingManagers] = useState(false);

  const fetchEligibleManagers = useCallback(async (targetEmpId) => {
    if (!targetEmpId) return;
    try {
      setLoadingManagers(true);
      const { data } = await axios.get(
        `${API_URL}/approval-workflows/eligible-managers?employee_id=${targetEmpId}&doc_type=local_travel_report`,
        { withCredentials: true }
      );
      const mgrList = data?.managers || [];
      setEligibleManagers(mgrList);
      if (mgrList.length > 0) {
        const defaultMgr = mgrList.find(m => m.hierarchy_level === 1 || m.relationship_to_employee === 'Reporting Manager') || mgrList[0];
        setSelectedApproverId(prev => {
          if (prev && mgrList.some(m => m.employee_id === prev)) return prev;
          return defaultMgr.employee_id;
        });
      }
    } catch (err) {
      console.error('Failed to fetch eligible approval managers:', err);
    } finally {
      setLoadingManagers(false);
    }
  }, []);

  useEffect(() => {
    const targetEmpId = !isSelfRequest && (beneficiaryEmployeeId || validatedBeneficiaryObj?.id)
      ? (beneficiaryEmployeeId || validatedBeneficiaryObj?.id)
      : profile?.employee_id;
    if (targetEmpId) {
      fetchEligibleManagers(targetEmpId);
    }
  }, [profile?.employee_id, isSelfRequest, beneficiaryEmployeeId, validatedBeneficiaryObj?.id, fetchEligibleManagers]);

  // Validate Beneficiary Employee by exact Employee ID
  const handleValidateBeneficiaryCode = async (codeInput) => {
    const codeToSearch = (codeInput || beneficiarySearchCodeInput || '').trim();
    if (!codeToSearch) {
      toast.error('Please enter a complete Employee ID.');
      return;
    }

    try {
      setValidatingBeneficiary(true);
      const { data } = await axios.get(
        `${API_URL}/travel-desk/validate-employee-code?code=${encodeURIComponent(codeToSearch)}`,
        { withCredentials: true }
      );

      const bId = String(data.employee_id || data.id);
      const bCode = data.employee_code || codeToSearch;
      const bName = data.employee_name || data.name || '';
      const bDept = data.department || '';
      const bDesig = data.designation || '';

      setBeneficiaryEmployeeId(bId);
      setBeneficiarySearchCodeInput(bCode);
      setValidatedBeneficiaryObj({
        id: bId,
        employee_id: bId,
        employee_code: bCode,
        full_name: bName,
        name: bName,
        department_name: bDept,
        department: bDept,
        designation_name: bDesig,
        designation: bDesig
      });

      toast.success(`Validated Beneficiary: ${bName} (${bCode})`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Employee not found. Please enter a valid Employee Code.';
      toast.error(msg);
      setBeneficiaryEmployeeId('');
      setValidatedBeneficiaryObj(null);
    } finally {
      setValidatingBeneficiary(false);
    }
  };

  // Fetch Employee Master Profile & Directory
  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/ess/local-travel/my-profile`, { withCredentials: true });
      if (data.profile) {
        setProfile(data.profile);
        fetchActivePolicy(reportDate, data.profile.company_id, data.profile.branch_id);
      }
    } catch {
      toast.error('Failed to load employee master profile');
    }

    try {
      const { data } = await axios.get(`${API_URL}/employees/minimal`, { withCredentials: true });
      setEmployeesDirectory(Array.isArray(data) ? data : (data?.data || []));
    } catch {
      setEmployeesDirectory([]);
    }
  };

  // Fetch Reports History
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/ess/local-travel?status=${filterStatus}&travel_type=${filterType}`;
      if (filterFromDate) url += `&from_date=${filterFromDate}`;
      if (filterToDate) url += `&to_date=${filterToDate}`;

      const { data } = await axios.get(url, { withCredentials: true });
      setReports(data.reports || []);
      if (data.summary) setSummary(data.summary);
    } catch {
      toast.error('Failed to load Local Travel history');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterFromDate, filterToDate]);

  // Fetch Active Policy Resolution
  const fetchActivePolicy = useCallback(async (dateStr, companyId = null, branchId = null) => {
    try {
      const cId = companyId || profile?.company_id || '';
      const bId = branchId || profile?.branch_id || '';
      let url = `${API_URL}/local-conveyance-policies/active-resolution?travel_date=${dateStr}`;
      if (cId) url += `&company_id=${encodeURIComponent(cId)}`;
      if (bId) url += `&branch_id=${encodeURIComponent(bId)}`;

      const { data } = await axios.get(url, { withCredentials: true });
      if (data.active_policy) {
        setActivePolicy(data.active_policy);
      } else {
        setActivePolicy(null);
      }
      if (data.date_validation) {
        setDateValidation(data.date_validation);
      } else if (data.active_policy?.date_validation) {
        setDateValidation(data.active_policy.date_validation);
      } else {
        setDateValidation(null);
      }
    } catch {
      setActivePolicy(null);
      setDateValidation(null);
    }
  }, [profile?.company_id, profile?.branch_id]);

  useEffect(() => {
    if (reportDate) {
      fetchActivePolicy(reportDate, profile?.company_id, profile?.branch_id);
    }
  }, [reportDate, profile?.company_id, profile?.branch_id, fetchActivePolicy]);

  // Backdated Application Days Limits & Calendar Validation Policy
  const calendarValidation = dateValidation || activePolicy?.date_validation;
  const countingMode = calendarValidation?.counting_mode || 'all_calendar_days';
  const effectiveLimitDays = calendarValidation?.limit_days !== undefined
    ? parseInt(calendarValidation.limit_days)
    : (activePolicy?.max_backdated_days !== undefined ? parseInt(activePolicy.max_backdated_days) : 2);
  const maxBackdatedDays = effectiveLimitDays;
  const todayStr = new Date().toISOString().split('T')[0];
  const computeMinDate = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
  };

  // Authoritative earliest allowed date from company's Calendar & Date Validation Policy
  const minAllowedDate = calendarValidation?.earliest_allowed_date || computeMinDate(maxBackdatedDays);

  // Mode configuration lookup
  const getModeConfig = useCallback((modeStr, overridePolicy = activePolicy) => {
    if (!modeStr) return { key: 'other_mode', isPerKm: false, rate_per_km: 0, max_daily_km: 0, max_daily_amount: 0, requires_bill: true };
    const val = modeStr.toLowerCase();
    let key = 'other_mode';
    if (val.includes('bike') || val.includes('two-wheeler')) key = 'own_bike';
    else if (val.includes('car') || val.includes('four-wheeler')) key = 'own_car';
    else if (val.includes('public') || val.includes('bus') || val.includes('train')) key = 'public_transport';
    else if (val.includes('auto') || val.includes('cab') || val.includes('taxi')) key = 'auto_cab';

    const r = overridePolicy?.rates?.[key] || {};
    const isPerKm = key === 'own_bike' || key === 'own_car';
    return {
      key,
      isPerKm,
      rate_per_km: isPerKm ? (r.rate_per_km !== undefined ? parseFloat(r.rate_per_km) : (key === 'own_car' ? 10 : 5)) : 0,
      max_daily_km: parseFloat(r.max_daily_km) || 0,
      max_daily_amount: parseFloat(r.max_daily_amount) || 0,
      // Policy-level requires_bill:false overrides any per-mode default or per-rate setting.
      // This ensures that when the admin marks the policy as "bills not required", no mode
      // incorrectly blocks submission due to a per-rate default.
      requires_bill: overridePolicy?.requires_bill === false
        ? false
        : (r.requires_bill !== undefined ? Boolean(r.requires_bill) : (!isPerKm)),
    };
  }, [activePolicy]);

  // Compute segment conveyance cost
  const computeSegmentCost = useCallback((modeStr, kmVal, currentCost, overridePolicy = activePolicy) => {
    const cfg = getModeConfig(modeStr, overridePolicy);
    if (!cfg.isPerKm) return parseFloat(currentCost) || 0;
    const km = parseFloat(kmVal) || 0;
    let eligibleKm = km;
    if (cfg.max_daily_km > 0 && km > cfg.max_daily_km) {
      eligibleKm = cfg.max_daily_km;
    }
    let fare = eligibleKm * cfg.rate_per_km;
    if (cfg.max_daily_amount > 0 && fare > cfg.max_daily_amount) {
      fare = cfg.max_daily_amount;
    }
    return Math.round(fare * 100) / 100;
  }, [getModeConfig, activePolicy]);

  // Handle updates to a segment (with auto-linking to next segment's from_location)
  const updateSegment = (idx, field, value) => {
    setSegments((prevSegments) => {
      const next = [...prevSegments];
      const current = { ...next[idx], [field]: value };
      const modeCfg = getModeConfig(current.travel_mode);

      current.rate_per_km = modeCfg.rate_per_km;

      // Odometer calculation
      if (field === 'start_odometer' || field === 'end_odometer') {
        const start = parseFloat(field === 'start_odometer' ? value : current.start_odometer) || 0;
        const end = parseFloat(field === 'end_odometer' ? value : current.end_odometer) || 0;
        if (end > 0 && start > 0) {
          if (end >= start) {
            current.distance_km = Math.round((end - start) * 10) / 10;
          } else {
            toast.error(`Segment #${idx + 1}: End Odometer cannot be lower than Start Odometer.`);
            current.distance_km = 0;
          }
        }
      }

      // Re-calculate cost if distance or mode changes
      if (field === 'distance_km' || field === 'start_odometer' || field === 'end_odometer' || field === 'travel_mode') {
        if (modeCfg.isPerKm) {
          current.conveyance_cost = computeSegmentCost(current.travel_mode, current.distance_km, current.conveyance_cost);
        }
      }

      next[idx] = current;

      // Auto-linking: If to_location updated, automatically propagate to next segment's from_location!
      if (field === 'to_location' && idx + 1 < next.length) {
        next[idx + 1] = {
          ...next[idx + 1],
          from_location: value,
        };
      }

      // Odometer auto-chaining: If end_odometer updated, propagate to next segment's start_odometer
      if (field === 'end_odometer' && idx + 1 < next.length && (!next[idx + 1].start_odometer || parseFloat(next[idx + 1].start_odometer) === 0)) {
        next[idx + 1] = {
          ...next[idx + 1],
          start_odometer: value,
        };
      }

      return next;
    });
  };

  // Add new travel segment (Google Maps style linked destination)
  const addSegment = () => {
    setSegments((prev) => {
      const lastSegment = prev[prev.length - 1];
      const autoFrom = lastSegment ? lastSegment.to_location : 'Head Office';
      const autoMode = lastSegment ? lastSegment.travel_mode : defaultTravelMode;
      const autoOdo = lastSegment ? (lastSegment.end_odometer || '') : '';

      const modeCfg = getModeConfig(autoMode);
      const newSeg = {
        segment_number: prev.length + 1,
        from_location: autoFrom,
        to_location: '',
        distance_km: '',
        travel_mode: autoMode,
        vehicle_number: vehicleNumber,
        start_odometer: autoOdo,
        end_odometer: '',
        rate_per_km: modeCfg.rate_per_km,
        conveyance_cost: 0,
      };

      toast.info(`Added Segment #${prev.length + 1} (From: ${autoFrom || 'Previous Destination'})`);
      return [...prev, newSeg];
    });
  };

  // Remove travel segment
  const removeSegment = (idx) => {
    if (segments.length === 1) {
      toast.error('At least one travel segment is required.');
      return;
    }
    setSegments((prev) => {
      const filtered = prev.filter((_, i) => i !== idx);
      // Re-link from_location for downstream segments
      for (let i = 0; i < filtered.length; i++) {
        filtered[i].segment_number = i + 1;
        if (i > 0) {
          filtered[i].from_location = filtered[i - 1].to_location;
        }
      }
      return filtered;
    });
    toast.info(`Removed Segment #${idx + 1}`);
  };

  // Global travel mode change -> update segments if needed
  const handleDefaultModeChange = (mode) => {
    setDefaultTravelMode(mode);
    setSegments((prev) =>
      prev.map((s) => {
        const modeCfg = getModeConfig(mode);
        const updated = { ...s, travel_mode: mode, rate_per_km: modeCfg.rate_per_km };
        if (modeCfg.isPerKm) {
          updated.conveyance_cost = computeSegmentCost(mode, updated.distance_km, updated.conveyance_cost);
        }
        return updated;
      })
    );
  };

  useEffect(() => {
    fetchProfile();
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    if (reportDate) fetchActivePolicy(reportDate);
  }, [reportDate, fetchActivePolicy]);

  // File Upload Handler
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setAttachments((prev) => [
          ...prev,
          {
            name: file.name,
            type: file.type,
            size: (file.size / 1024).toFixed(1) + ' KB',
            data_url: event.target.result,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
    toast.success(`${files.length} document(s) attached`);
  };

  const removeAttachment = (idx) => {
    setAttachments(attachments.filter((_, i) => i !== idx));
  };

  // Form Reset
  const resetForm = () => {
    setEditingId(null);
    setReworkMode({ isRework: false, returnRemarks: '', sentBackBy: '', originalReportNumber: '' });
    setReapplicationMode({ isReapplication: false, rejectedFromId: '', reportNumber: '', rejectionReason: '' });
    setReworkNotes('');
    setReportDate(new Date().toISOString().slice(0, 10));
    setTravelPurpose('Local Travel');
    setDefaultTravelMode('Own Two-Wheeler');
    setVehicleNumber('');
    setSegments([
      {
        segment_number: 1,
        from_location: 'Head Office',
        to_location: '',
        distance_km: '',
        travel_mode: 'Own Two-Wheeler',
        vehicle_number: '',
        start_odometer: '',
        end_odometer: '',
        rate_per_km: 5,
        conveyance_cost: '',
      },
    ]);
    setFoodExpense(0);
    setParkingExpense(0);
    setTollExpense(0);
    setOtherExpense(0);
    setOtherExpenseDesc('');
    setAttachments([]);
  };

  // Helper to load report data into form fields
  const loadReportIntoForm = (rep) => {
    setReportDate(rep.report_date || new Date().toISOString().slice(0, 10));
    setTravelPurpose(rep.travel_purpose || 'Local Travel');
    setDefaultTravelMode(rep.travel_mode || 'Own Two-Wheeler');
    setVehicleNumber(rep.vehicle_number || '');

    const loadedSegments = (rep.segments && rep.segments.length > 0)
      ? rep.segments
      : (rep.stops && rep.stops.length > 0
          ? rep.stops.map((s, idx) => ({
              segment_number: idx + 1,
              from_location: s.from_location || rep.from_location || '',
              to_location: s.to_location || s.location_name || '',
              distance_km: s.total_km || s.kilometers || 0,
              travel_mode: s.travel_mode || rep.travel_mode || 'Own Two-Wheeler',
              vehicle_number: s.vehicle_number || rep.vehicle_number || '',
              start_odometer: s.start_odometer || 0,
              end_odometer: s.end_odometer || 0,
              rate_per_km: s.rate_per_km || 0,
              conveyance_cost: s.fare_amount || s.bill_amount || 0,
            }))
          : [
              {
                segment_number: 1,
                from_location: rep.from_location || 'Head Office',
                to_location: rep.to_location || '',
                distance_km: rep.total_km || 0,
                travel_mode: rep.travel_mode || 'Own Two-Wheeler',
                vehicle_number: rep.vehicle_number || '',
                start_odometer: rep.start_odometer || '',
                end_odometer: rep.end_odometer || '',
                rate_per_km: 0,
                conveyance_cost: rep.conveyance_amount || 0,
              },
            ]
        );

    setSegments(loadedSegments);
    setFoodExpense(rep.expenses?.food_expense || rep.food_amount || 0);
    setParkingExpense(rep.expenses?.parking || 0);
    setTollExpense(rep.expenses?.toll || 0);
    setOtherExpense(rep.other_amount || 0);
    setOtherExpenseDesc(rep.other_amount_desc || '');
    setAttachments(rep.attachments || []);
  };

  // Edit Existing Draft Report
  const editDraftReport = (rep) => {
    resetForm();
    setEditingId(rep.id || rep._id);
    loadReportIntoForm(rep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info(`Editing draft report for ${rep.report_date}`);
  };

  // Rework Sent-Back Report
  const reworkReport = (rep) => {
    resetForm();
    setEditingId(rep.id || rep._id);
    setReworkMode({
      isRework: true,
      returnRemarks: rep.return_remarks || 'Returned for correction',
      sentBackBy: rep.sent_back_by || 'Approving Manager',
      originalReportNumber: rep.report_number || rep.id || rep._id,
    });
    setReworkNotes(rep.rework_notes || '');
    loadReportIntoForm(rep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info(`Rework Mode: Updating claim for ${rep.report_date}`);
  };

  // Reapply from Rejected Report
  const reapplyReport = (rep) => {
    resetForm();
    setEditingId(null);
    setReapplicationMode({
      isReapplication: true,
      rejectedFromId: rep.id || rep._id,
      reportNumber: rep.report_number || rep.id || rep._id,
      rejectionReason: rep.rejection_reason || 'Rejected by Manager / Finance',
    });
    loadReportIntoForm(rep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info(`Reapplication Mode: Pre-filled from rejected claim #${rep.report_number || ''}`);
  };

  // Live Totals Across Segments & Expenses
  const overallTotalKm = segments.reduce((acc, s) => acc + (parseFloat(s.distance_km) || 0), 0);
  const totalConveyanceAmount = segments.reduce((acc, s) => acc + (parseFloat(s.conveyance_cost) || 0), 0);
  const totalFoodExpense = parseFloat(foodExpense) || 0;
  const totalParkingTollExpense = (parseFloat(parkingExpense) || 0) + (parseFloat(tollExpense) || 0);
  const totalOtherExpense = parseFloat(otherExpense) || 0;
  const grandTotalClaimed = totalConveyanceAmount + totalFoodExpense + totalParkingTollExpense + totalOtherExpense;

  // Primary Route Summary (e.g. Head Office → Client A → Client B → Branch Office)
  const routePoints = [];
  if (segments.length > 0 && segments[0].from_location) routePoints.push(segments[0].from_location);
  segments.forEach((s) => {
    if (s.to_location) routePoints.push(s.to_location);
  });
  const connectedRouteStr = routePoints.join(' → ');

  // Save / Submit Handler
  const handleSave = async (status) => {
    if (!reportDate) {
      toast.error('Please select Report Date.');
      return;
    }

    if (reportDate > todayStr) {
      toast.error(`This date (${reportDate}) is outside the permitted Local Conveyance application period. Future travel dates are not permitted.`);
      return;
    }

    if (reportDate < minAllowedDate) {
      const modeText = countingMode === 'working_days_only'
        ? 'working day(s) (weekly offs and holidays excluded)'
        : (countingMode === 'working_days_and_holidays'
          ? 'working day(s) & holiday(s) (weekly offs excluded)'
          : (countingMode === 'working_days_and_weekoffs'
            ? 'working day(s) & weekly off(s) (holidays excluded)'
            : 'calendar day(s)'));
      const errorMsg = calendarValidation?.message || `This date (${reportDate}) is outside the permitted Local Conveyance application period. You can apply for today and up to ${effectiveLimitDays} ${modeText} (earliest allowed date: ${minAllowedDate}).`;
      toast.error(errorMsg);
      return;
    }

    if (status === 'submitted') {
      if (segments.length === 0 || !segments[0].from_location.trim() || !segments[0].to_location.trim()) {
        toast.error('Please enter From Location and To Location for Segment #1.');
        return;
      }
      for (let i = 0; i < segments.length; i++) {
        const s = segments[i];
        if (!s.to_location.trim()) {
          toast.error(`Please enter destination (To Location) for Segment #${i + 1}.`);
          return;
        }
        // Only enforce bill attachment when an active policy is configured AND the
        // mode-specific rate configuration explicitly requires a bill. Without a
        // configured policy, submission is allowed without a bill (the backend will
        // also accept it in that case).
        const cfg = getModeConfig(s.travel_mode);
        // Enforce bill only when BOTH the policy-level flag AND the mode-level flag require it.
        // If the active policy has requires_bill: false at the policy level, skip enforcement
        // regardless of what individual rate configs say (policy-level is the authoritative override).
        const policyRequiresBill = activePolicy?.requires_bill !== false && cfg.requires_bill;
        if (activePolicy && policyRequiresBill && attachments.length === 0) {
          toast.error(`Supporting bill/receipt attachment is mandatory for '${s.travel_mode}' under your company's active Local Conveyance Policy.`);
          return;
        }
      }
    }

    try {
      setSaving(true);
      const payload = {
        report_date: reportDate,
        travel_purpose: travelPurpose,
        is_self_request: isSelfRequest,
        requested_on_behalf: !isSelfRequest,
        beneficiary_employee_id: !isSelfRequest ? beneficiaryEmployeeId : null,
        selected_approver_id: selectedApproverId,
        from_location: segments[0]?.from_location || '',
        to_location: segments[0]?.to_location || '',
        additional_destinations: segments.slice(1).map((s) => s.to_location).filter(Boolean),
        travel_mode: defaultTravelMode,
        vehicle_number: vehicleNumber.trim(),
        start_odometer: parseFloat(segments[0]?.start_odometer) || 0,
        end_odometer: parseFloat(segments[segments.length - 1]?.end_odometer) || 0,
        total_km: overallTotalKm,
        conveyance_amount: totalConveyanceAmount,
        food_amount: totalFoodExpense,
        parking_toll_amount: totalParkingTollExpense,
        other_amount: totalOtherExpense,
        other_amount_desc: otherExpenseDesc,
        segments: segments,
        expenses: {
          food_expense: parseFloat(foodExpense) || 0,
          tea_refreshment: 0,
          parking: parseFloat(parkingExpense) || 0,
          toll: parseFloat(tollExpense) || 0,
          other_expense: parseFloat(otherExpense) || 0,
          other_expense_desc: otherExpenseDesc,
        },
        totals: {
          total_travel_expense: totalConveyanceAmount,
          total_food_expense: totalFoodExpense,
          total_other_expense: totalParkingTollExpense + totalOtherExpense,
          grand_total: grandTotalClaimed,
          total_km: overallTotalKm,
        },
        attachments,
        status,
      };

      if (reapplicationMode.isReapplication) {
        payload.reapplied_from_id = reapplicationMode.rejectedFromId;
        payload.reapplied_from_report_number = reapplicationMode.reportNumber;
        payload.is_reapplied = true;
        payload.reapply_notes = `Reapplied from rejected claim #${reapplicationMode.reportNumber}`;
      }

      if (reworkMode.isRework) {
        payload.rework_notes = reworkNotes || 'Claim updated and resubmitted after rework';
      }

      if (editingId) {
        if (reworkMode.isRework && status === 'submitted') {
          await axios.put(`${API_URL}/ess/local-travel/${editingId}/resubmit`, payload, { withCredentials: true });
          toast.success('Report resubmitted for approval after rework!');
        } else {
          await axios.put(`${API_URL}/ess/local-travel/${editingId}`, payload, { withCredentials: true });
          toast.success(`Report updated ${status === 'draft' ? 'as Draft' : 'and submitted for approval'}!`);
        }
      } else {
        await axios.post(`${API_URL}/ess/local-travel`, payload, { withCredentials: true });
        toast.success(
          reapplicationMode.isReapplication
            ? 'New claim submitted successfully from reapplication!'
            : `Report ${status === 'draft' ? 'saved as Draft' : 'submitted for approval'}!`
        );
      }

      resetForm();
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save Local Travel report');
    } finally {
      setSaving(false);
    }
  };

  // Cancel / Withdraw Submitted Claim
  const handleCancelReport = async (rep) => {
    const repId = rep.id || rep._id;
    if (!window.confirm(`Are you sure you want to cancel and withdraw Local Conveyance claim (${rep.report_number || rep.report_date})?`)) return;
    try {
      await axios.post(`${API_URL}/ess/local-travel/${repId}/cancel`, { reason: 'Cancelled by employee' }, { withCredentials: true });
      toast.success('Claim cancelled and withdrawn successfully');
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel claim');
    }
  };

  // Delete Draft / Sent-Back Report
  const handleDeleteReport = async (rep) => {
    const repId = rep.id || rep._id;
    if (!window.confirm(`Are you sure you want to delete report (${rep.report_number || rep.report_date})?`)) return;
    try {
      await axios.delete(`${API_URL}/ess/local-travel/${repId}`, { withCredentials: true });
      toast.success('Report deleted successfully');
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete report');
    }
  };

  // Download PDF Receipt
  const downloadPdf = async (repId) => {
    try {
      const res = await axios.get(`${API_URL}/ess/local-travel/${repId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `LocalTravel_${repId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF download failed');
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-7xl mx-auto pb-16 font-sans" data-testid="ess-local-travel-page">
        {/* Top Title Banner */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/30 shrink-0">
              <Navigation className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight">Local Conveyance &amp; On-Duty Travel</h1>
              <p className="text-xs text-blue-200 mt-0.5">
                Single-step daily report with linked locations, segment-wise KM &amp; policy conveyance fare.
              </p>
            </div>
          </div>
          <div className="text-right text-xs text-slate-300">
            <p><span className="text-slate-400">Employee Code:</span> <b>{profile.employee_code}</b></p>
            <p className="mt-0.5"><span className="text-slate-400">Department:</span> <b>{profile.department_name}</b></p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-2xl p-2 border border-slate-200 shadow-sm flex items-center gap-2">
          <button
            onClick={() => setMainTab('my_claims')}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              mainTab === 'my_claims'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <Navigation className="w-4 h-4" /> My Local Conveyance Claims
          </button>
          <button
            onClick={() => setMainTab('approval_inbox')}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              mainTab === 'approval_inbox'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <UserCheck className="w-4 h-4" /> Approval Inbox
          </button>
        </div>

        {mainTab === 'approval_inbox' ? (
          <LocalTravelApprovalInbox isEmbedded={true} />
        ) : (
          <>
            {/* Rework Banner */}
            {reworkMode.isRework && (
              <div className="bg-gradient-to-r from-orange-500/15 via-amber-500/10 to-orange-500/15 border-2 border-orange-400 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-slate-800" data-testid="rework-banner">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-orange-100 text-orange-700 rounded-xl shrink-0 mt-0.5">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-black uppercase tracking-wider bg-orange-600 text-white px-2 py-0.5 rounded">Rework Mode</span>
                      <span className="text-xs font-bold text-orange-900">Report #{reworkMode.originalReportNumber}</span>
                    </div>
                    <p className="text-xs text-slate-700 mt-1 font-medium">
                      Sent back by <b>{reworkMode.sentBackBy || 'Approving Manager'}</b>:
                      <span className="italic text-orange-950 font-semibold ml-1">"{reworkMode.returnRemarks}"</span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Make required corrections below. When ready, click <b>Resubmit Claim</b> to send back into the approval workflow.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="text-xs border-orange-300 text-orange-800 hover:bg-orange-100 shrink-0 font-bold"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel Rework
                </Button>
              </div>
            )}

            {/* Reapplication Banner */}
            {reapplicationMode.isReapplication && (
              <div className="bg-gradient-to-r from-purple-500/15 via-indigo-500/10 to-purple-500/15 border-2 border-purple-400 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-slate-800" data-testid="reapply-banner">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 text-purple-700 rounded-xl shrink-0 mt-0.5">
                    <Plus className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-black uppercase tracking-wider bg-purple-600 text-white px-2 py-0.5 rounded">Reapplication Mode</span>
                      <span className="text-xs font-bold text-purple-900">Reapplying from Rejected Claim #{reapplicationMode.reportNumber}</span>
                    </div>
                    <p className="text-xs text-slate-700 mt-1 font-medium">
                      Previous Rejection Reason:
                      <span className="italic text-rose-900 font-semibold ml-1">"{reapplicationMode.rejectionReason}"</span>
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      A brand new claim record will be created. The historical rejected record remains preserved for audit compliance.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="text-xs border-purple-300 text-purple-800 hover:bg-purple-100 shrink-0 font-bold"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel Reapplication
                </Button>
              </div>
            )}

            {/* SECTION 1: MASTER EMPLOYEE DETAILS & BENEFICIARY LINK */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="section-employee-master font-sans">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Users className="w-4 h-4 text-[#0052FF]" /> 1. Employee Details &amp; Claim Ownership
            </h3>
            <span className="text-[10px] bg-blue-50 text-[#0052FF] font-bold px-2.5 py-1 rounded-full border border-blue-200">
              Requester &amp; Beneficiary Matrix
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-3 rounded-xl border border-slate-200 mb-2 items-end">
            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1.5">Claim Mode *</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs font-bold text-slate-800 cursor-pointer">
                  <input
                    type="radio"
                    name="local_claim_mode"
                    checked={isSelfRequest}
                    onChange={() => {
                      setIsSelfRequest(true);
                      setBeneficiaryEmployeeId('');
                      setBeneficiarySearchCodeInput('');
                      setValidatedBeneficiaryObj(null);
                    }}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  Self Claim (For Myself)
                </label>
                <label className="flex items-center gap-2 text-xs font-bold text-slate-800 cursor-pointer">
                  <input
                    type="radio"
                    name="local_claim_mode"
                    checked={!isSelfRequest}
                    onChange={() => {
                      setIsSelfRequest(false);
                      setBeneficiaryEmployeeId('');
                      setValidatedBeneficiaryObj(null);
                    }}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  On-Behalf Claim (For Another Employee)
                </label>
              </div>
            </div>

            {!isSelfRequest && (
              <div>
                <label className="block text-xs font-bold text-indigo-900 mb-1">Enter Beneficiary Employee ID *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="e.g. S2248"
                    value={beneficiarySearchCodeInput}
                    onChange={(e) => setBeneficiarySearchCodeInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleValidateBeneficiaryCode();
                      }
                    }}
                    className="flex-1 px-3 py-1.5 bg-white border border-indigo-300 rounded-lg text-xs font-mono font-bold text-indigo-950 outline-none focus:border-indigo-600 uppercase"
                    data-testid="input-local-beneficiary-id"
                  />
                  <button
                    type="button"
                    onClick={() => handleValidateBeneficiaryCode()}
                    disabled={validatingBeneficiary}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-xs rounded-lg transition flex items-center gap-1 shrink-0 shadow-sm"
                    data-testid="btn-validate-local-beneficiary"
                  >
                    {validatingBeneficiary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Validate ID
                  </button>
                </div>
              </div>
            )}
          </div>

          {!isSelfRequest && (validatedBeneficiaryObj || beneficiaryEmployeeId) && (() => {
            const bEmp = validatedBeneficiaryObj || employeesDirectory.find(e => String(e.id || e._id || e.employee_id) === String(beneficiaryEmployeeId));
            const bName = bEmp?.full_name || bEmp?.name || bEmp?.employee_name || 'Validated Beneficiary';
            const bCode = bEmp?.employee_code || bEmp?.code || beneficiarySearchCodeInput || 'N/A';
            const bDept = bEmp?.department_name || bEmp?.department || profile.department_name || 'N/A';
            const bDesig = bEmp?.designation_name || bEmp?.designation || profile.designation_name || 'N/A';
            return (
              <div className="p-3.5 bg-indigo-50/80 rounded-xl border border-indigo-200 text-indigo-950 text-xs font-bold space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="flex items-center gap-2 text-indigo-900 font-extrabold text-sm">
                    <UserCheck className="w-4 h-4 text-indigo-600" />
                    Beneficiary / Claim Owner: <span className="underline">{bName}</span> ({bCode})
                  </span>
                  <span className="text-[10px] bg-emerald-600 text-white font-extrabold px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Validated (Claim Owner)
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1 border-t border-indigo-200/60 text-slate-700 text-[11px] font-normal">
                  <div><span className="font-semibold text-indigo-900">Code:</span> {bCode}</div>
                  <div><span className="font-semibold text-indigo-900">Department:</span> {bDept}</div>
                  <div><span className="font-semibold text-indigo-900">Designation:</span> {bDesig}</div>
                  <div><span className="font-semibold text-indigo-900">Requester:</span> {profile.employee_name} ({profile.employee_code})</div>
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Requester Name</span>
              <span className="font-bold text-slate-800">{profile.employee_name}</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Requester Code</span>
              <span className="font-bold text-slate-800 font-mono">{profile.employee_code}</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Company</span>
              <span className="font-bold text-slate-800">{profile.company_name}</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Department</span>
              <span className="font-bold text-slate-800">{profile.department_name}</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Designation</span>
              <span className="font-bold text-slate-800">{profile.designation_name}</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-bold">Reporting Manager</span>
              <span className="font-bold text-slate-800">{profile.reporting_manager_name}</span>
            </div>
            <div className="sm:col-span-2">
              <label className="text-slate-700 block text-[10px] uppercase font-bold mb-1">Report Date * (1 Report / Date)</label>
              <input
                type="date"
                min={minAllowedDate}
                max={todayStr}
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-800 outline-none focus:border-blue-500 bg-slate-50"
                data-testid="input-report-date"
                required
              />
              <div className="mt-1 flex flex-col gap-0.5 text-[11px] font-semibold text-indigo-700 bg-indigo-50/80 px-2.5 py-1.5 rounded-lg border border-indigo-200">
                <div className="flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                  <span>
                    Allowed Application Dates: {minAllowedDate} to {todayStr} (Today &amp; up to {effectiveLimitDays} {countingMode === 'working_days_only' ? 'working day(s)' : (countingMode.includes('holidays') || countingMode.includes('weekoffs') ? 'eligible day(s)' : 'day(s)')})
                  </span>
                </div>
                {(countingMode === 'working_days_only' || countingMode === 'working_days_and_holidays' || countingMode === 'working_days_and_weekoffs' || calendarValidation?.details?.excluded_weekoff_dates?.length > 0 || calendarValidation?.details?.excluded_holiday_dates?.length > 0) && (
                  <div className="text-[10px] text-indigo-600 pl-5 font-normal">
                    <span className="font-semibold capitalize">{countingMode.replace(/_/g, ' ')}:</span>
                    {countingMode === 'working_days_only' && ' Weekly offs and public holidays do not consume allowed days.'}
                    {countingMode === 'working_days_and_holidays' && ' Weekly offs do not consume allowed days.'}
                    {countingMode === 'working_days_and_weekoffs' && ' Holidays do not consume allowed days.'}
                    {calendarValidation?.details?.excluded_weekoff_dates?.length > 0 && ` Excluded ${calendarValidation.details.excluded_weekoff_dates.length} weekly off(s).`}
                    {calendarValidation?.details?.excluded_holiday_dates?.length > 0 && ` Excluded ${calendarValidation.details.excluded_holiday_dates.length} holiday(s).`}
                  </div>
                )}
              </div>
            </div>

            <div className="sm:col-span-2">
              <ManagerApprovalSelector
                selectedApproverId={selectedApproverId}
                onSelectApprover={(empId, mgrObj) => setSelectedApproverId(empId)}
                eligibleManagers={eligibleManagers}
                targetEmployeeId={profile.employee_id}
                loadingManagers={loadingManagers}
                docType="local_travel_report"
                label="Manager Approval Selection (Local Conveyance) *"
              />
            </div>
          </div>

          {/* Active Policy Info Badge */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs bg-indigo-50/50 p-3 rounded-xl border border-indigo-100">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              <span className="font-bold text-slate-700">Active Company Policy:</span>
              <span className="font-semibold text-indigo-700">
                {activePolicy
                  ? `${activePolicy.policy_name} (v${activePolicy.version || 1}) — Scope: ${activePolicy.company_name || 'Company Scope'}`
                  : `No Specific Policy Configured for ${profile.company_name || 'Company'} (Default Rates Active)`}
              </span>
            </div>
            <div className="flex items-center space-x-3 text-[11px] text-slate-600 flex-wrap">
              <span>Own Bike: <strong className="text-slate-800">₹{activePolicy?.rates?.own_bike?.rate_per_km || 5}/KM</strong></span>
              <span>•</span>
              <span>Own Car: <strong className="text-slate-800">₹{activePolicy?.rates?.own_car?.rate_per_km || 10}/KM</strong></span>
              <span>•</span>
              <span>Cab/Auto: <strong className="text-slate-800">₹{activePolicy?.rates?.auto_cab?.rate_per_km || 12}/KM</strong></span>
            </div>
          </div>
        </div>

        {/* SECTION 2: PURPOSE & DEFAULT TRAVEL MODE */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="section-duty-purpose">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Navigation className="w-4 h-4 text-[#0052FF]" /> 2. Travel Purpose &amp; Vehicle Mode
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div>
              <label className="block font-semibold text-slate-700 mb-1">Purpose of visit/Client details *</label>
              <input
                type="text"
                placeholder="Enter purpose of visit / duty details..."
                value={travelPurpose}
                onChange={(e) => setTravelPurpose(e.target.value)}
                className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-xs font-semibold text-slate-800 outline-none focus:border-blue-500"
                data-testid="input-duty-type"
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-1">Primary Travel Mode *</label>
              <select
                value={defaultTravelMode}
                onChange={(e) => handleDefaultModeChange(e.target.value)}
                className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-xs font-semibold outline-none focus:border-blue-500"
                data-testid="select-travel-mode"
              >
                {TRAVEL_MODES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-1">Vehicle Number (Optional)</label>
              <input
                type="text"
                placeholder="e.g. TN-09-AB-1234"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className="w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-xs font-mono uppercase outline-none focus:border-blue-500"
                data-testid="input-vehicle-number"
              />
            </div>
          </div>
        </div>

        {/* SECTION 3: LINKED TRAVEL LOCATIONS (GOOGLE MAPS STYLE) */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="section-linked-locations">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" /> 3. Linked Travel Locations &amp; Segment-Wise Distance
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Each new destination automatically links its From Location to the previous destination.
              </p>
            </div>
            <Button
              type="button"
              onClick={addSegment}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-extrabold px-4 py-2 rounded-xl transition flex items-center gap-1.5 shadow-sm"
              data-testid="btn-add-destination"
            >
              <Plus className="w-4 h-4" /> Add Destination / Segment
            </Button>
          </div>

          {/* Linked Segment Cards */}
          <div className="space-y-4">
            {segments.map((seg, idx) => {
              const segCfg = getModeConfig(seg.travel_mode);
              const isFirst = idx === 0;

              return (
                <div
                  key={idx}
                  className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4 shadow-sm space-y-3 relative"
                  data-testid={`segment-card-${idx}`}
                >
                  <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-black text-[11px] flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="font-bold text-slate-900 text-xs">
                        Segment #{idx + 1}: {seg.from_location || 'Origin'} &rarr; {seg.to_location || 'Destination'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${segCfg.isPerKm ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                        {segCfg.isPerKm ? `Per-KM (₹${segCfg.rate_per_km}/KM)` : 'Actual Bill'}
                      </span>
                      {segments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSegment(idx)}
                          className="text-rose-600 hover:text-rose-800 p-1 font-bold text-xs flex items-center gap-1"
                          title="Remove Segment"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    {/* From Location */}
                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">
                        From Location {isFirst ? '*' : <span className="text-emerald-700 font-bold">(Auto-Linked)</span>}
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Head Office"
                        value={seg.from_location}
                        onChange={(e) => updateSegment(idx, 'from_location', e.target.value)}
                        readOnly={!isFirst}
                        className={`w-full border rounded-lg px-3 py-1.5 text-xs font-bold ${
                          isFirst
                            ? 'border-slate-300 bg-white text-slate-800 focus:border-indigo-500 outline-none'
                            : 'border-emerald-200 bg-emerald-50/70 text-emerald-950 cursor-not-allowed'
                        }`}
                        data-testid={`input-from-location-${idx}`}
                        required
                      />
                    </div>

                    {/* To Location */}
                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">To Location (Destination) *</label>
                      <input
                        type="text"
                        placeholder="e.g. Client Site A"
                        value={seg.to_location}
                        onChange={(e) => updateSegment(idx, 'to_location', e.target.value)}
                        className="w-full border border-slate-300 bg-white rounded-lg px-3 py-1.5 text-xs font-bold text-slate-900 outline-none focus:border-indigo-500"
                        data-testid={`input-to-location-${idx}`}
                        required
                      />
                    </div>

                    {/* Segment Travel Mode */}
                    <div>
                      <label className="block font-semibold text-slate-700 mb-1">Segment Travel Mode</label>
                      <select
                        value={seg.travel_mode}
                        onChange={(e) => updateSegment(idx, 'travel_mode', e.target.value)}
                        className="w-full border border-slate-300 bg-white rounded-lg px-2.5 py-1.5 text-xs font-semibold outline-none"
                      >
                        {TRAVEL_MODES.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>

                    {/* Distance KM / Bill Amount */}
                    {segCfg.isPerKm ? (
                      <div>
                        <label className="block font-semibold text-slate-700 mb-1">Distance (KM) *</label>
                        <input
                          type="number"
                          placeholder="e.g. 10"
                          value={seg.distance_km}
                          onChange={(e) => updateSegment(idx, 'distance_km', e.target.value)}
                          className="w-full border border-slate-300 bg-white rounded-lg px-3 py-1.5 text-xs font-mono font-bold text-slate-900 outline-none focus:border-indigo-500"
                          data-testid={`input-distance-km-${idx}`}
                          required
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block font-semibold text-slate-700 mb-1">Actual Bill / Cost (₹) *</label>
                        <input
                          type="number"
                          placeholder="e.g. 250"
                          value={seg.conveyance_cost}
                          onChange={(e) => updateSegment(idx, 'conveyance_cost', e.target.value)}
                          className="w-full border border-slate-300 bg-white rounded-lg px-3 py-1.5 text-xs font-mono font-bold text-slate-900 outline-none focus:border-indigo-500"
                          data-testid={`input-conveyance-cost-${idx}`}
                          required
                        />
                      </div>
                    )}
                  </div>

                  {/* Odometer Inputs (Optional for Per-KM Own Vehicles) */}
                  {segCfg.isPerKm && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-200/60 text-xs">
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">Start Odometer (Optional)</label>
                        <input
                          type="number"
                          placeholder="1000"
                          value={seg.start_odometer}
                          onChange={(e) => updateSegment(idx, 'start_odometer', e.target.value)}
                          className="w-full border border-slate-300 bg-white rounded-lg px-2.5 py-1 text-xs font-mono outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">End Odometer (Optional)</label>
                        <input
                          type="number"
                          placeholder="1010"
                          value={seg.end_odometer}
                          onChange={(e) => updateSegment(idx, 'end_odometer', e.target.value)}
                          className="w-full border border-slate-300 bg-white rounded-lg px-2.5 py-1 text-xs font-mono outline-none"
                        />
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 pt-4">
                        <span className="text-[11px] text-slate-500">Segment Fare:</span>
                        <span className="font-mono font-black text-emerald-700 text-sm bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                          ₹ {(parseFloat(seg.conveyance_cost) || 0).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Connected Route Preview Banner */}
          {connectedRouteStr && (
            <div className="bg-indigo-50/80 border border-indigo-200 p-3 rounded-xl text-xs text-indigo-950 font-medium flex items-center gap-2 overflow-x-auto">
              <span className="font-bold text-indigo-900 shrink-0">Connected Route Flow:</span>
              <span className="font-mono font-bold text-indigo-700">{connectedRouteStr}</span>
            </div>
          )}
        </div>

        {/* SECTION 4: LIVE SEGMENT ROUTE & CONVEYANCE SUMMARY TABLE */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="section-segment-summary-table">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Table className="w-4 h-4 text-[#0052FF]" /> 4. Live Segment Route &amp; Conveyance Summary Table
            </h3>
            <span className="text-[10px] bg-emerald-50 text-emerald-800 font-bold px-2.5 py-1 rounded-full border border-emerald-200">
              Live Policy Calculation
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border border-slate-200 rounded-xl overflow-hidden">
              <thead className="bg-slate-100 text-[11px] uppercase font-bold text-slate-700 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2.5">Segment #</th>
                  <th className="px-3 py-2.5">From Location</th>
                  <th className="px-3 py-2.5">To Location</th>
                  <th className="px-3 py-2.5">Travel Mode</th>
                  <th className="px-3 py-2.5 text-right">Distance (KM)</th>
                  <th className="px-3 py-2.5 text-right">Policy Rate</th>
                  <th className="px-3 py-2.5 text-right">Calculated Cost (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {segments.map((seg, idx) => {
                  const cfg = getModeConfig(seg.travel_mode);
                  return (
                    <tr key={idx} className="hover:bg-slate-50 transition">
                      <td className="px-3 py-2.5 font-bold font-mono text-indigo-700">Segment #{idx + 1}</td>
                      <td className="px-3 py-2.5 font-semibold text-slate-900">{seg.from_location || '-'}</td>
                      <td className="px-3 py-2.5 font-semibold text-slate-900">{seg.to_location || '-'}</td>
                      <td className="px-3 py-2.5 text-slate-700">{seg.travel_mode}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold">{seg.distance_km || 0} KM</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-600">
                        {cfg.isPerKm ? `₹${cfg.rate_per_km}/KM` : 'Actual Bill'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono font-black text-emerald-700">
                        ₹ {(parseFloat(seg.conveyance_cost) || 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-900 text-white font-mono text-xs font-bold">
                <tr>
                  <td colSpan={4} className="px-3 py-3 uppercase tracking-wider">Overall Route Summary ({segments.length} Segments)</td>
                  <td className="px-3 py-3 text-right text-amber-400 font-black">{overallTotalKm} KM</td>
                  <td className="px-3 py-3 text-right text-slate-400">-</td>
                  <td className="px-3 py-3 text-right text-emerald-400 font-black text-sm">
                    ₹ {totalConveyanceAmount.toLocaleString('en-IN')}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* SECTION 5: FOOD, TOLL, PARKING & OTHER EXPENSES */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="section-other-expenses font-sans">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#0052FF]" /> 5. Food, Parking, Toll &amp; Other Expenses
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div>
              <label className="block font-semibold text-slate-700 mb-1">Food / Meal Expense (₹)</label>
              <input
                type="number"
                placeholder="0.00"
                value={foodExpense}
                onChange={(e) => setFoodExpense(parseFloat(e.target.value) || 0)}
                className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono outline-none"
                data-testid="input-food-expense"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-1">Parking Charges (₹)</label>
              <input
                type="number"
                placeholder="0.00"
                value={parkingExpense}
                onChange={(e) => setParkingExpense(parseFloat(e.target.value) || 0)}
                className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono outline-none"
                data-testid="input-parking"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-1">Toll Charges (₹)</label>
              <input
                type="number"
                placeholder="0.00"
                value={tollExpense}
                onChange={(e) => setTollExpense(parseFloat(e.target.value) || 0)}
                className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono outline-none"
                data-testid="input-toll"
              />
            </div>

            <div className="sm:col-span-1">
              <label className="block font-semibold text-slate-700 mb-1">Other Official Expense (₹)</label>
              <input
                type="number"
                placeholder="0.00"
                value={otherExpense}
                onChange={(e) => setOtherExpense(parseFloat(e.target.value) || 0)}
                className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono outline-none"
                data-testid="input-other-expense"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block font-semibold text-slate-700 mb-1">Remarks / Description of Expenses</label>
              <input
                type="text"
                placeholder="Details of client visit, purpose, or reason for other expense..."
                value={otherExpenseDesc}
                onChange={(e) => setOtherExpenseDesc(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs outline-none"
                data-testid="input-other-desc"
              />
            </div>
          </div>

          {/* Bill Attachment Box */}
          <div className="pt-3 border-t border-slate-100 space-y-2">
            <label className="block font-bold text-slate-800 text-xs flex items-center justify-between">
              <span>Supporting Expense Bills &amp; Receipts</span>
            </label>

            <div className="flex items-center gap-3">
              <label className="cursor-pointer bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition">
                <Upload className="w-4 h-4 text-blue-600" /> Choose Receipts / Bills
                <input type="file" multiple onChange={handleFileUpload} className="hidden" accept="image/*,.pdf" />
              </label>
              <span className="text-[11px] text-slate-400">Supports JPG, PNG, PDF (Max 5MB per file)</span>
            </div>

            {attachments.length > 0 && (
              <div className="space-y-1.5 pt-2">
                {attachments.map((att, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-blue-50/50 border border-blue-200 rounded-lg text-xs">
                    <span className="font-semibold text-slate-800 truncate">{att.name} ({att.size})</span>
                    <button type="button" onClick={() => removeAttachment(idx)} className="text-rose-600 font-bold hover:underline">
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SECTION 6: LIVE EXPENSE CATEGORY CARDS & GRAND TOTAL */}
        <div className="bg-slate-900 text-white rounded-2xl p-6 shadow-xl space-y-5 font-sans" data-testid="live-totals-card">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-3">
            <div>
              <span className="text-xs text-blue-400 font-bold uppercase tracking-wider block">6. Claim Expense Summary &amp; Grand Total</span>
              <p className="text-xs text-slate-400 mt-0.5">
                Category-wise expense breakdown and automatic live Grand Total calculation.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSave('draft')}
                disabled={saving}
                className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold px-4 py-2 rounded-xl"
                data-testid="btn-save-draft"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save Draft
              </Button>
              <Button
                type="button"
                onClick={() => handleSave('submitted')}
                disabled={saving}
                className={`text-white text-xs font-extrabold px-6 py-2 rounded-xl shadow-lg transition ${
                  reworkMode.isRework
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : reapplicationMode.isReapplication
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
                data-testid="btn-submit-report"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : reworkMode.isRework ? (
                  <RefreshCw className="w-4 h-4 mr-1" />
                ) : reapplicationMode.isReapplication ? (
                  <Plus className="w-4 h-4 mr-1" />
                ) : (
                  <Send className="w-4 h-4 mr-1" />
                )}
                {reworkMode.isRework ? 'Resubmit Reworked Claim' : reapplicationMode.isReapplication ? 'Submit Reapplied Claim' : 'Submit Report'}
              </Button>
            </div>
          </div>

          {/* 4 Expense Category Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="expense-category-cards">
            {/* Card 1: Travel & Conveyance */}
            <div className="bg-slate-800/90 border border-slate-700 rounded-xl p-4 space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-400 block">1. Travel &amp; Conveyance</span>
              <p className="text-xl font-bold font-mono text-white">
                ₹ {totalConveyanceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-slate-400">Route conveyance fare ({overallTotalKm} KM)</p>
            </div>

            {/* Card 2: Food & Refreshments */}
            <div className="bg-slate-800/90 border border-slate-700 rounded-xl p-4 space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400 block">2. Food &amp; Refreshments</span>
              <p className="text-xl font-bold font-mono text-white">
                ₹ {totalFoodExpense.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-slate-400">Meal, tea &amp; refreshment expenses</p>
            </div>

            {/* Card 3: Parking & Toll */}
            <div className="bg-slate-800/90 border border-slate-700 rounded-xl p-4 space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 block">3. Parking &amp; Toll</span>
              <p className="text-xl font-bold font-mono text-white">
                ₹ {totalParkingTollExpense.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-slate-400">Parking fees &amp; highway toll charges</p>
            </div>

            {/* Card 4: Other Expenses */}
            <div className="bg-slate-800/90 border border-slate-700 rounded-xl p-4 space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400 block">4. Other Expenses</span>
              <p className="text-xl font-bold font-mono text-white">
                ₹ {totalOtherExpense.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-slate-400">Other approved official expenses</p>
            </div>
          </div>

          {/* Dedicated Grand Total Section */}
          <div className="bg-gradient-to-r from-amber-500/10 via-amber-400/20 to-amber-500/10 border border-amber-500/40 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="grand-total-section">
            <div>
              <span className="text-xs font-bold text-amber-400 uppercase tracking-wider block">Grand Total Claim Amount</span>
              <p className="text-[11px] text-slate-300 mt-0.5">
                Grand Total = Travel &amp; Conveyance + Food &amp; Refreshments + Parking &amp; Toll + Other Expenses
              </p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-black font-mono text-amber-400" data-testid="grand-total-value">
                ₹ {grandTotalClaimed.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* SECTION 7: MY REPORTS HISTORY & MONTHLY SUMMARY */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-testid="section-history-table">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#0052FF]" /> My Local Conveyance History &amp; Monthly Summary
            </h3>
            <div className="flex items-center gap-2">
              <Button onClick={fetchReports} variant="outline" className="text-xs py-1 px-3">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
            </div>
          </div>

          {/* STATUS FILTER PILLS */}
          <div className="flex items-center gap-1.5 flex-wrap pb-1" data-testid="status-filter-pills">
            {[
              { key: 'all', label: 'All Claims' },
              { key: 'pending', label: 'Pending Review' },
              { key: 'sent_back', label: 'Sent Back / Rework' },
              { key: 'approved', label: 'Approved' },
              { key: 'rejected', label: 'Rejected' },
              { key: 'draft', label: 'Draft' },
            ].map((tab) => {
              const active = filterStatus === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setFilterStatus(tab.key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    active
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                  data-testid={`filter-tab-${tab.key}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* SUMMARY STATS BAR */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-xs" data-testid="monthly-summary-cards">
            <div className="bg-slate-50 border p-3 rounded-xl"><span className="text-slate-400 block text-[10px] uppercase font-bold">On-Duty Days</span><p className="text-sm font-bold text-slate-800">{summary.total_on_duty_days}</p></div>
            <div className="bg-slate-50 border p-3 rounded-xl"><span className="text-slate-400 block text-[10px] uppercase font-bold">Total KM</span><p className="text-sm font-bold text-slate-800">{summary.total_km} KM</p></div>
            <div className="bg-slate-50 border p-3 rounded-xl"><span className="text-slate-400 block text-[10px] uppercase font-bold">Total Conveyance</span><p className="text-sm font-bold text-slate-800">₹ {summary.total_conveyance}</p></div>
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl"><span className="text-blue-700 block text-[10px] uppercase font-bold">Approved</span><p className="text-sm font-bold text-blue-900">₹ {summary.approved_amount}</p></div>
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl"><span className="text-amber-700 block text-[10px] uppercase font-bold">Pending Review</span><p className="text-sm font-bold text-amber-900">₹ {summary.pending_amount}</p></div>
            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-xl"><span className="text-emerald-700 block text-[10px] uppercase font-bold">Paid</span><p className="text-sm font-bold text-emerald-900">₹ {summary.paid_amount || 0}</p></div>
            <div className="bg-slate-900 text-white p-3 rounded-xl col-span-2 sm:col-span-1"><span className="text-slate-400 block text-[10px] uppercase font-bold">Total Claimed</span><p className="text-sm font-bold text-amber-400">₹ {summary.total_claimed}</p></div>
          </div>

          {/* TABLE */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-xs italic">
                No Local Conveyance records found.
              </div>
            ) : (
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 text-[11px] uppercase font-bold text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2.5">Date / Ref</th>
                    <th className="px-3 py-2.5">Purpose</th>
                    <th className="px-3 py-2.5">Linked Route Flow</th>
                    <th className="px-3 py-2.5">Mode</th>
                    <th className="px-3 py-2.5 text-right">Overall Total KM</th>
                    <th className="px-3 py-2.5 text-right">Claim Amount (₹)</th>
                    <th className="px-3 py-2.5 text-center">Status</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reports.map((r) => {
                    const st = (r.status || 'DRAFT').toUpperCase();
                    const appSt = (r.approval_status || '').toLowerCase();
                    const isDraft = st === 'DRAFT';
                    const isSentBack = st === 'SENT_BACK' || st === 'RETURNED' || appSt === 'sent_back';
                    const isRejected = st === 'REJECTED' || appSt === 'rejected';
                    const isSubmitted = st === 'SUBMITTED' || st === 'FINANCE_REVIEW' || appSt === 'in_progress';
                    const routeStr = [r.from_location, r.to_location, ...(r.additional_destinations || [])].filter(Boolean).join(' → ') || 'Local Route';

                    return (
                      <tr key={r.id || r._id} className="hover:bg-slate-50/80 transition" data-testid={`row-report-${r.id}`}>
                        <td className="px-3 py-3 font-bold text-slate-800 font-mono whitespace-nowrap">
                          {r.report_date}
                          {r.report_number && (
                            <span className="block text-[10px] text-slate-400 font-mono font-normal">{r.report_number}</span>
                          )}
                          {r.reapplied_from_report_number && (
                            <span className="inline-block text-[9px] bg-purple-50 text-purple-700 border border-purple-200 px-1 py-0.2 rounded font-sans font-semibold">
                              From #{r.reapplied_from_report_number}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 font-semibold text-slate-800 max-w-xs truncate">{r.travel_purpose}</td>
                        <td className="px-3 py-3 text-slate-700 max-w-xs truncate font-mono text-[11px]" title={routeStr}>{routeStr}</td>
                        <td className="px-3 py-3 text-slate-700 font-medium whitespace-nowrap">{r.travel_mode}</td>
                        <td className="px-3 py-3 text-right font-mono font-bold">{r.total_km || 0} KM</td>
                        <td className="px-3 py-3 text-right font-mono font-black text-[#0052FF]">
                          ₹ {(r.total_amount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <StatusBadge status={isSentBack ? 'SENT_BACK' : r.status} />
                          {isSentBack && r.return_remarks && (
                            <p className="text-[10px] text-orange-700 italic truncate max-w-[160px] mx-auto mt-0.5" title={r.return_remarks}>
                              Note: {r.return_remarks}
                            </p>
                          )}
                          {isRejected && r.rejection_reason && (
                            <p className="text-[10px] text-rose-700 italic truncate max-w-[160px] mx-auto mt-0.5" title={r.rejection_reason}>
                              Reason: {r.rejection_reason}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            <button
                              type="button"
                              onClick={() => setViewingReport(r)}
                              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg flex items-center gap-1 text-[11px]"
                              title="View full claim breakdown"
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>

                            {isDraft && (
                              <button
                                type="button"
                                onClick={() => editDraftReport(r)}
                                className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold rounded-lg flex items-center gap-1 text-[11px]"
                                title="Edit draft claim"
                              >
                                <Edit className="w-3.5 h-3.5" /> Edit
                              </button>
                            )}

                            {isSentBack && (
                              <button
                                type="button"
                                onClick={() => reworkReport(r)}
                                className="px-2.5 py-1 bg-orange-100 hover:bg-orange-200 text-orange-800 font-bold rounded-lg flex items-center gap-1 text-[11px]"
                                title="Rework and resubmit claim"
                              >
                                <RefreshCw className="w-3.5 h-3.5" /> Rework
                              </button>
                            )}

                            {isRejected && (
                              <button
                                type="button"
                                onClick={() => reapplyReport(r)}
                                className="px-2.5 py-1 bg-purple-100 hover:bg-purple-200 text-purple-800 font-bold rounded-lg flex items-center gap-1 text-[11px]"
                                title="Reapply as a fresh claim"
                              >
                                <Plus className="w-3.5 h-3.5" /> Reapply
                              </button>
                            )}

                            {(isSubmitted || isSentBack) && (
                              <button
                                type="button"
                                onClick={() => handleCancelReport(r)}
                                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg flex items-center gap-1 text-[11px]"
                                title="Cancel / Withdraw claim"
                              >
                                <XCircle className="w-3.5 h-3.5" /> Cancel
                              </button>
                            )}

                            {(isDraft || isSentBack) && (
                              <button
                                type="button"
                                onClick={() => handleDeleteReport(r)}
                                className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold rounded-lg flex items-center gap-1 text-[11px]"
                                title="Delete report"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => downloadPdf(r.id)}
                              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold rounded-lg text-[11px]"
                              title="Download PDF"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* VIEW DETAILS MODAL */}
        {viewingReport && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="view-report-modal">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl space-y-4 p-6 border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-slate-900">Local Conveyance Claim — {viewingReport.report_date}</h3>
                  <StatusBadge status={viewingReport.status} />
                </div>
                <button type="button" onClick={() => setViewingReport(null)} className="text-slate-400 hover:text-slate-600 p-1 rounded-md">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Master Info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs">
                <div><span className="text-slate-400 block text-[10px]">Employee</span><b>{viewingReport.employee_name} ({viewingReport.employee_code})</b></div>
                <div><span className="text-slate-400 block text-[10px]">Company / Branch</span><b>{viewingReport.company_name} ({viewingReport.branch_name})</b></div>
                <div><span className="text-slate-400 block text-[10px]">Department</span><b>{viewingReport.department_name}</b></div>
                <div><span className="text-slate-400 block text-[10px]">Submitted Date</span><b>{(viewingReport.submitted_at || viewingReport.created_at || '').slice(0, 10)}</b></div>
              </div>

              {/* Connected Route Summary */}
              <div className="bg-indigo-50/70 border border-indigo-200 p-3.5 rounded-xl text-xs space-y-2">
                <span className="font-bold uppercase text-indigo-900 tracking-wider flex items-center gap-1">
                  <Navigation className="w-4 h-4 text-indigo-600" /> Linked Route Flow
                </span>
                <p className="font-mono font-bold text-slate-900 text-sm">
                  {[viewingReport.from_location, viewingReport.to_location, ...(viewingReport.additional_destinations || [])].filter(Boolean).join(' → ')}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] pt-1">
                  <div><span className="text-slate-500 block">Travel Mode:</span><b>{viewingReport.travel_mode}</b></div>
                  <div><span className="text-slate-500 block">Vehicle Number:</span><b>{viewingReport.vehicle_number || 'N/A'}</b></div>
                  <div><span className="text-slate-500 block">Odometer Readout:</span><b>{viewingReport.start_odometer || 0} → {viewingReport.end_odometer || 0}</b></div>
                  <div><span className="text-slate-500 block">Overall Total KM:</span><b className="text-indigo-700">{viewingReport.total_km || 0} KM</b></div>
                </div>
              </div>

              {/* Segment-Wise Breakdown Table */}
              {viewingReport.segments && viewingReport.segments.length > 0 && (
                <div className="space-y-2 text-xs">
                  <h4 className="font-bold uppercase text-slate-700 tracking-wider">Segment-Wise Conveyance Breakdown</h4>
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 font-bold text-slate-700 border-b">
                        <tr>
                          <th className="px-3 py-2">Seg #</th>
                          <th className="px-3 py-2">From Location</th>
                          <th className="px-3 py-2">To Location</th>
                          <th className="px-3 py-2 text-right">KM</th>
                          <th className="px-3 py-2 text-right">Cost (₹)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {viewingReport.segments.map((s, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2 font-mono font-bold text-indigo-700">#{idx + 1}</td>
                            <td className="px-3 py-2">{s.from_location}</td>
                            <td className="px-3 py-2 font-semibold">{s.to_location}</td>
                            <td className="px-3 py-2 text-right font-mono">{s.distance_km} KM</td>
                            <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">₹ {(parseFloat(s.conveyance_cost) || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sent Back / Return Remarks Banner */}
              {(viewingReport.status === 'SENT_BACK' || viewingReport.return_remarks) && (
                <div className="bg-orange-50 border border-orange-300 p-3.5 rounded-xl text-xs text-orange-950 font-medium space-y-1" data-testid="detail-return-remarks">
                  <p className="font-bold text-orange-900 flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    Sent Back for Correction {viewingReport.sent_back_by ? `by ${viewingReport.sent_back_by}` : ''}:
                  </p>
                  <p className="mt-0.5 text-orange-900 font-semibold">{viewingReport.return_remarks || 'Claim returned for rework'}</p>
                  {viewingReport.sent_back_at && (
                    <p className="text-[10px] text-orange-700">Date: {viewingReport.sent_back_at.slice(0, 19).replace('T', ' ')}</p>
                  )}
                </div>
              )}

              {/* Rejection / Payment Detail Banners */}
              {viewingReport.status === 'REJECTED' && viewingReport.rejection_reason && (
                <div className="bg-rose-50 border border-rose-300 p-3.5 rounded-xl text-xs text-rose-950 font-medium">
                  <p className="font-bold text-rose-900">Rejection Reason:</p>
                  <p className="mt-0.5">{viewingReport.rejection_reason}</p>
                </div>
              )}

              {viewingReport.status === 'PAID' && (
                <div className="bg-emerald-50 border border-emerald-300 p-3.5 rounded-xl text-xs text-emerald-950 space-y-1 font-medium">
                  <p className="font-bold text-emerald-900">Payment Completed Details:</p>
                  <p>Paid Date: <b>{viewingReport.paid_date}</b> &middot; Mode: <b>{viewingReport.payment_mode}</b> &middot; Ref: <b>{viewingReport.transaction_ref}</b> &middot; Paid Amount: <b>₹ {(viewingReport.paid_amount || 0).toLocaleString('en-IN')}</b></p>
                </div>
              )}

              {/* Attachments */}
              {viewingReport.attachments && viewingReport.attachments.length > 0 && (
                <div className="space-y-2 text-xs">
                  <h4 className="font-bold uppercase text-slate-700 tracking-wider">Uploaded Expense Receipts</h4>
                  <div className="space-y-1.5">
                    {viewingReport.attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 border rounded-lg text-xs">
                        <span className="font-semibold text-slate-800 truncate">{att.name}</span>
                        <a href={att.data_url} download={att.name} className="text-blue-600 font-bold hover:underline flex items-center gap-1">
                          <Download className="w-3.5 h-3.5" /> Download Bill
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expense Totals */}
              <div className="bg-slate-900 text-white rounded-xl p-4 text-xs space-y-2 font-mono">
                <div className="flex justify-between"><span>Conveyance Amount:</span><span>₹ {(viewingReport.conveyance_amount || 0).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between"><span>Food / Meal Amount:</span><span>₹ {(viewingReport.food_amount || 0).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between"><span>Parking &amp; Toll Charges:</span><span>₹ {(viewingReport.parking_toll_amount || 0).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between"><span>Other Expenses:</span><span>₹ {(viewingReport.other_amount || 0).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-amber-400 font-bold border-t border-slate-800 pt-2 text-sm">
                  <span>TOTAL CLAIMED AMOUNT:</span><span>₹ {(viewingReport.total_amount || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={() => downloadPdf(viewingReport.id)}>
                  <Download className="w-4 h-4 mr-1.5" /> Download PDF
                </Button>
                <Button type="button" onClick={() => setViewingReport(null)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </MainLayout>
  );
}
